import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useCompanyInfo } from "@/context/CompanyInfoContext";

const ThemeContext = createContext({ theme: "dark", toggle: () => {} });
const KEY = "lt_theme";

function deriveAccent(hex) {
  // Slightly brighten hex for hover / bright fallbacks used across the site.
  const h = hex?.replace("#", "") || "8B1E2D";
  const n = parseInt(h, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const clamp = (x) => Math.min(255, Math.max(0, Math.round(x)));
  const toHex = (x) => x.toString(16).padStart(2, "0").toUpperCase();
  const hover = `#${toHex(clamp(r * 1.12))}${toHex(clamp(g * 1.12))}${toHex(clamp(b * 1.12))}`;
  const bright = `#${toHex(clamp(r * 1.35 + 20))}${toHex(clamp(g * 1.35 + 15))}${toHex(clamp(b * 1.35 + 15))}`;
  return { hover, bright };
}

export function ThemeProvider({ children }) {
  const { info } = useCompanyInfo();
  const [theme, setTheme] = useState(() => localStorage.getItem(KEY) || "light");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(KEY, theme);
  }, [theme]);

  // Apply accent color from company info as a CSS variable — updates everywhere instantly.
  useEffect(() => {
    const accent = info?.accent_color || "#8B1E2D";
    const { hover, bright } = deriveAccent(accent);
    const root = document.documentElement;
    root.style.setProperty("--lt-accent", accent);
    root.style.setProperty("--lt-accent-hover", hover);
    root.style.setProperty("--lt-accent-bright", bright);
  }, [info?.accent_color]);

  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
